import React, { useEffect, useState } from "react";
import Navbar from "../../components/Navbar/Navbar";
import Property from "../../components/Listing/Listing";
import Footer from "../../components/Footer/Footer";
import Loading from "../../components/Loading/Loading";
import styles from "./SelectHouseView.module.css";
import axios from "axios";
import { Grid } from "@mui/material";
import { Button } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import SelectHousePropertyFilter from "../../components/SelectHousePropertyFilter/SelectHousePropertyFilter";
import DeletePropertyModal from "../../components/dashbaord/dashboardUtil/DeletePropertyModal/DeletePropertyModal";
import PropertyMapView from "../../components/Map/PropertyMapView";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";


const SelectHouseView = () => {

  const checkAddressAlreadyListed = async (filters) => {
    try {
      const queryString = toQueryString(filters);
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/listings/fetchByAddress?${queryString}`
      );

      // If exact address is found
      if (Array.isArray(response.data) && response.data.length > 0) {
        generateDynamicUrl(response.data[0]);
        return true;
      }

      // No exact address found — try fallback keys
      const fallbackKeys = ["zipcode", "city", "state"];
      for (const key of fallbackKeys) {
        const value = filters[key]?.trim();
        if (!value) continue;

        const searchParams = { ...filters };
        delete searchParams.city;
        delete searchParams.state;
        delete searchParams.zipcode;
        searchParams[key] = value;

        try {
          const res = await axios.get(
            `${import.meta.env.VITE_BACKEND_URL}/listings/search`,
            { params: searchParams }
          );

          if (Array.isArray(res.data.listings) && res.data.listings.length > 0) {
            setAllListings(sortAndAlternateListings(res.data.listings, key));
            setFallbackInfo({ type: key, value });

            toast.info(
              `No listings found for the exact address. Showing properties for ${key} "${value}".`
            );
            return false;
          }
        } catch (err) {
          console.error(`Error searching by ${key}:`, err);
        }
      }

      // Final fallback – show listings across the country
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_BACKEND_URL}/listings/search`,
          { params: {} }
        );

        setAllListings(sortAndAlternateListings(res.data.listings, "all"));
        setFallbackInfo({ type: "all", value: "United States" });

        toast.info(
          `No listings found for the exact address. Showing properties across the country.`
        );
      } catch (err) {
        console.error("Error fetching all listings:", err);
      }

    } catch (error) {
      console.error("Error checking address:", error);
      toast.error("An error occurred while checking the address.");
      return false;
    }
  };


  const capitalizeFirstLetter = (s) => s.charAt(0).toUpperCase() + s.slice(1);

  const generateDynamicUrl = (listing) => {
    const baseUrl = listing.url || "";
    if (listing.sell_price) {
      window.open(`/Buy-Homes/${baseUrl}`, "_self");
    } else if (listing.rent_price) {
      window.open(`/Rent-Homes/${baseUrl}`, "_self");
    } else {
      window.open(`/${baseUrl}`, "_blank"); // fallback if neither price exists
    }
  };

  const toQueryString = (params) =>
    Object.entries(params)
      .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
      .join("&");


  const sortAndAlternateListings = (listings) => {
    const sortedListings = listings.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    const premium = sortedListings.filter(l => l.is_premium);
    const normal = sortedListings.filter(l => !l.is_premium);
    return [...premium, ...normal];
  };


  // Fetch listings data
  const fetchListingsData = async (filters = []) => {
    try {
      const queryString = toQueryString(filters);
      setAddressInputState(filters.addressInput);
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/listings/allFilter?${queryString}`
      );

      if (response.status === 200 && response.data.success) {
        const listings = response.data.listings;

        if (accessToken) {
          const listingsWithFavorites = await fetchFavorites(listings);
          // console.log("listingsWithFavorites: ", listingsWithFavorites);
          setAllListings(sortAndAlternateListings(listingsWithFavorites));
        } else {
          // If not logged in, just display the listings
          setAllListings(sortAndAlternateListings(listings));
        }
        // const alternatedListings = sortAndAlternateListings(
        //   response.data.listings
        // );
        // setAllListings(alternatedListings);
        // console.log(alternatedListings);
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  const fetchFavorites = async (listings) => {
    try {
      const favoritesResponse = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/listings/favorites/`,
        {},
        {
          headers: { Authorization: accessToken },
        }
      );

      let favoriteListingIds = [];
      if (
        favoritesResponse.status === 200 &&
        favoritesResponse.data.success &&
        Array.isArray(favoritesResponse.data.favorites)
      ) {
        favoriteListingIds = favoritesResponse.data.favorites; // favorites is already an array of IDs
      }

      // Mark listings as favorite
      const listingsWithFavorites = listings.map((listing) => ({
        ...listing,
        isFavorite: favoriteListingIds.includes(listing.id),
      }));

      // console.log("Listings with favorites marked:", listingsWithFavorites);

      return listingsWithFavorites;
    } catch (error) {
      console.error("Error fetching favorites:", error);
      return listings; // Return original listings if there's an error
    }
  };

  // Open the delete modal
  const openDeleteModal = (listing) => {
    setSelectedListing(listing);
    setIsDeleteModalOpen(true);
  };

  // Close the delete modal
  const closeDeleteModal = () => {
    setSelectedListing(null);
    setIsDeleteModalOpen(false);
  };

  // Handle delete property function with reason
  const handleDeleteProperty = async (id, reason) => {
    try {
      const response = await axios.delete(
        `${import.meta.env.VITE_BACKEND_URL}/listings/${id}`,
        {
          data: { reason }, // Send reason in the delete request body
        }
      );

      if (response.status === 200) {
        setAllListings((prevListings) =>
          prevListings.filter((listing) => listing.id !== id)
        );
        toast.success("Property deleted successfully.");
      } else {
        toast.error("Failed to delete property.");
      }
      closeDeleteModal();
    } catch (error) {
      console.error("Error deleting property:", error);
      closeDeleteModal();
    }
  };

  const handleApplyFilters = (filterData) => {
    fetchListingsData(filterData);
  };

  

  const handleToggleChange = (event, newView) => {
    if (newView) {
      setViewMode(newView);
    }
  };

  const location = useLocation();
  const { fullAddress, zip, city, state } = location.state || {};

  const { accessToken } = useAuth();

  const [allListings, setAllListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState("map"); // Toggle between "list" and "map"
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [addressInputState, setAddressInputState] = useState("");
  const [fallbackInfo, setFallbackInfo] = useState(null); 

  useEffect(() => {
    if (location.state) {
      checkAddressAlreadyListed(location.state);
    }
    setIsLoading(false);
  }, [location.state]);

  // checkAddressAlreadyListed(location.state);

  return (
    <>
      <Navbar />
      <div className={styles.title}>
        <h1>Choose your dream home!</h1>
      </div>

      {/* Toggle between List and Map */}
      <Grid container justifyContent="center" sx={{ marginBottom: "30px" }}>
        <div className={styles.toggleContainer}>
          {/* List View Button */}
          <Button
            variant={viewMode === "list" ? "contained" : "outlined"}
            onClick={() => setViewMode("list")}
            className={viewMode === "list" ? styles.listSelected : ""}
          >
            <ListIcon />
            List
          </Button>

          {/* Map View Button */}
          <Button
            variant={viewMode === "map" ? "contained" : "outlined"}
            onClick={() => setViewMode("map")}
            className={viewMode === "map" ? styles.mapSelected : ""}
          >
            <MapIcon />
            Map
          </Button>
        </div>
      </Grid>

      {isLoading ? (
        <div className={styles.buyAndSellLoading}>
          <Loading />
        </div>
      ) : (
        <>
          <SelectHousePropertyFilter onApplyFilters={handleApplyFilters} fallbackInfo={fallbackInfo} />
          <div className={styles.viewContent}>
            {viewMode === "list" ? (
              <>
                {allListings.map((listing, index) => (
                  <Property key={index} listing={listing} />
                ))}
              </>
            ) : (
              <div className={styles.mapPlaceholder}>
                {allListings && allListings.length ? (
                  <PropertyMapView
                    listings={allListings}
                    addressInputState={addressInputState}
                  />
                ) : (
                  <div>There are no properties available in this area!</div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      <Footer />

      {/* Delete Property Modal */}
      {isDeleteModalOpen && (
        <DeletePropertyModal
          listing={selectedListing}
          onClose={closeDeleteModal}
          onDelete={handleDeleteProperty}
        />
      )}
    </>
  );
};

export default SelectHouseView;
